import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import {
  Bars3Icon,
  BellIcon,
  MagnifyingGlassIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DocumentTextIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  TagIcon,
  SunIcon,
  MoonIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline'
import { dashboardAPI, searchAPI, notificationsAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import useLanguage from '../../hooks/useLanguage'
import useTheme from '../../hooks/useTheme'
import { formatNumber } from '../../utils/formatters'
import NotificationsPanel from './NotificationsPanel'

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

const SearchResultItem = ({ item, section, onClick }) => {
  let title = ''
  let subtitle = ''

  switch (section) {
    case 'documents':
      title = item.title
      subtitle = [item.category?.name, item.document_type].filter(Boolean).join(' · ')
      break
    case 'expenses':
      title = item.title
      subtitle = [
        item.vendor,
        `${parseFloat(item.amount).toFixed(2)} ${item.currency || 'PLN'}`,
      ].filter(Boolean).join(' · ')
      break
    case 'incomes':
      title = item.title
      subtitle = [
        item.source,
        `${parseFloat(item.amount).toFixed(2)} ${item.currency || 'PLN'}`,
      ].filter(Boolean).join(' · ')
      break
    case 'categories':
      title = item.name
      subtitle = ''
      break
    default:
      title = item.title || item.name
  }

  return (
    <button
      type="button"
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors hover:bg-black/5 dark:hover:bg-white/5"
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: 'var(--color-text)' }}>
          {title}
        </p>
        {subtitle && (
          <p className="text-xs truncate mt-0.5" style={{ color: 'var(--color-text-faint)' }}>
            {subtitle}
          </p>
        )}
      </div>
    </button>
  )
}

const Header = ({ onMenuClick }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation('common')
  const { locale } = useLanguage()
  const { user, logout } = useAuth()
  const { isDark, setTheme } = useTheme()
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showNotifications, setShowNotifications] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const searchRef = useRef(null)
  const userMenuRef = useRef(null)

  const debouncedQuery = useDebounce(searchQuery, 300)

  const PAGE_TITLES = {
    '/': t('pageTitles./'),
    '/documents': t('pageTitles./documents'),
    '/documents/upload': t('pageTitles./documents/upload'),
    '/categories': t('pageTitles./categories'),
    '/budget/expenses': t('pageTitles./budget/expenses'),
    '/budget/incomes': t('pageTitles./budget/incomes'),
    '/budget/budgets': t('pageTitles./budget/budgets'),
    '/budget/goals': t('pageTitles./budget/goals'),
    '/budget/reports': t('pageTitles./budget/reports'),
    '/profile': t('pageTitles./profile'),
    '/settings': t('pageTitles./settings'),
    // legacy
    '/expenses': t('pageTitles./budget/expenses'),
    '/budgets': t('pageTitles./budget/budgets'),
    '/reports': t('pageTitles./budget/reports'),
  }

  const SECTION_CONFIG = [
    { key: 'documents', label: t('sections.documents'), Icon: DocumentTextIcon, color: '#3B82F6' },
    { key: 'expenses', label: t('sections.expenses'), Icon: BanknotesIcon, color: '#EF4444' },
    { key: 'incomes', label: t('sections.incomes'), Icon: CurrencyDollarIcon, color: '#10B981' },
    { key: 'categories', label: t('sections.categories'), Icon: TagIcon, color: '#8B5CF6' },
  ]

  // Resolve page title
  const path = location.pathname
  let pageTitle = PAGE_TITLES[path]
  if (!pageTitle) {
    // handle /documents/:id
    if (path.startsWith('/documents/') && path !== '/documents/upload') {
      pageTitle = t('document')
    } else {
      pageTitle = t('appName')
    }
  }

  // Fetch dashboard stats for the financial strip
  const { data: dashboardData } = useQuery(
    'dashboard',
    dashboardAPI.getDashboard,
    { refetchInterval: 30000, staleTime: 15000 }
  )

  // Unread notifications count
  const { data: unreadData } = useQuery(
    'notificationsUnread',
    notificationsAPI.getUnreadCount,
    { refetchInterval: 10000, staleTime: 5000 }
  )

  // Search query
  const { data: searchData, isLoading: searchLoading, isError: searchError } = useQuery(
    ['search', debouncedQuery],
    () => searchAPI.search(debouncedQuery),
    {
      enabled: showSearch && debouncedQuery.length >= 2,
      staleTime: 10000,
      keepPreviousData: true,
    }
  )

  const results = searchData?.data
  const hasResults = results && results.total > 0

  const stats = dashboardData?.data?.stats
  const incomeThisMonth = stats?.income?.this_month || 0
  const expensesThisMonth = stats?.expenses?.this_month || 0
  const balance = incomeThisMonth - expensesThisMonth

  const budgetAlerts = dashboardData?.data?.budget_alerts || []
  const overBudgetCount = budgetAlerts.filter(a => a.spent_percentage > 100).length
  const unreadNotifications = unreadData?.data?.count || 0

  const closeSearch = useCallback(() => {
    setShowSearch(false)
    setSearchQuery('')
  }, [])

  const handleResultClick = useCallback((section, item) => {
    switch (section) {
      case 'documents':
        navigate(`/documents/${item.id}`)
        break
      case 'expenses':
        navigate('/budget/expenses')
        break
      case 'incomes':
        navigate('/budget/incomes')
        break
      case 'categories':
        navigate('/categories')
        break
    }
    closeSearch()
  }, [navigate, closeSearch])

  // Keyboard shortcut: Ctrl+K / Cmd+K for search
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setShowSearch(true)
        setTimeout(() => searchRef.current?.focus(), 50)
      }
      if (e.key === 'Escape') {
        closeSearch()
        setShowUserMenu(false)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [closeSearch])

  // Close user menu on outside click
  useEffect(() => {
    if (!showUserMenu) return
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showUserMenu])

  return (
    <>
      <div className="flex items-center justify-between gap-4 px-5 py-4 lg:px-6">
        {/* Left: Mobile menu + Page title + finance strip */}
        <div className="flex items-center gap-4 min-w-0">
          <button
            type="button"
            className="lg:hidden p-2 -m-2 rounded-xl"
            style={{ color: 'var(--color-text)' }}
            onClick={onMenuClick}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <h2 className="m-0 text-[22px] font-black tracking-[.2px] shrink-0" style={{ color: 'var(--color-text)' }}>
            {pageTitle}
          </h2>

          {/* Financial strip - only on non-dashboard pages */}
          {stats && path !== '/' && (
            <div
              className="hidden md:flex items-center gap-1 ml-2 px-3 py-[6px] rounded-full"
              style={{
                background: 'var(--color-glass)',
                border: '1px solid var(--color-border-strong)',
                fontSize: '12px',
                fontWeight: 800,
              }}
            >
              <span style={{ color: 'var(--color-text-faint)' }}>{t('dashboard:balance')}</span>
              <span style={{ color: balance >= 0 ? '#167e75' : '#e55353' }}>
                {formatNumber(balance, locale)} zł
              </span>

              <span style={{ color: 'var(--color-separator)', margin: '0 4px' }}>|</span>

              <ArrowTrendingDownIcon className="h-3 w-3" style={{ color: 'var(--color-icon-muted)' }} />
              <span style={{ color: 'var(--color-text-faint)' }}>{formatNumber(expensesThisMonth, locale)} zł</span>

              <span style={{ color: 'var(--color-separator)', margin: '0 4px' }}>|</span>

              <ArrowTrendingUpIcon className="h-3 w-3" style={{ color: 'var(--color-icon-muted)' }} />
              <span style={{ color: 'var(--color-text-faint)' }}>{formatNumber(incomeThisMonth, locale)} zł</span>

              {overBudgetCount > 0 && (
                <>
                  <span style={{ color: 'var(--color-separator)', margin: '0 4px' }}>|</span>
                  <span
                    className="px-1.5 py-0.5 rounded-full"
                    style={{ background: 'var(--color-error-bg)', color: '#e55353', fontSize: '11px' }}
                  >
                    {t('exceeded', { count: overBudgetCount })}
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-[10px] shrink-0">
          {/* Theme toggle */}
          <button
            className="w-[42px] h-[42px] rounded-[14px] grid place-items-center cursor-pointer"
            style={{
              border: '1px solid var(--color-border)',
              background: 'var(--color-glass)',
              boxShadow: 'var(--shadow-btn)',
            }}
            title={isDark ? t('themeLight') : t('themeDark')}
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
          >
            {isDark ? (
              <SunIcon className="h-[18px] w-[18px]" style={{ color: 'var(--color-text-faint)' }} />
            ) : (
              <MoonIcon className="h-[18px] w-[18px]" style={{ color: 'var(--color-text-faint)' }} />
            )}
          </button>

          {/* Notification bell */}
          <div className="relative">
            <button
              className="w-[42px] h-[42px] rounded-[14px] grid place-items-center cursor-pointer"
              style={{
                border: '1px solid var(--color-border)',
                background: showNotifications ? 'rgba(31,159,147,.08)' : 'var(--color-glass)',
                boxShadow: 'var(--shadow-btn)',
              }}
              title={t('notifications')}
              onClick={() => setShowNotifications((v) => !v)}
            >
              <div className="relative">
                <BellIcon className="h-[18px] w-[18px]" style={{ color: showNotifications ? '#1f9f93' : 'var(--color-text-faint)' }} />
                {unreadNotifications > 0 && (
                  <span
                    className="absolute top-[14px] -right-[12px] min-w-[16px] h-[16px] rounded-full flex items-center justify-center text-[10px] font-bold leading-none px-[3px]"
                    style={{
                      background: '#1f9f93',
                      color: '#fff',
                      border: '2px solid var(--color-dot-border)',
                    }}
                  >
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
              </div>
            </button>
            {showNotifications && (
              <NotificationsPanel
                onClose={() => setShowNotifications(false)}
                locale={locale}
              />
            )}
          </div>

          {/* Search pill */}
          <div
            className="flex items-center gap-[10px] px-3 py-[10px] rounded-full cursor-pointer"
            style={{
              background: 'var(--color-glass)',
              border: '1px solid var(--color-border)',
              boxShadow: 'var(--shadow-btn)',
            }}
            onClick={() => {
              setShowSearch(true)
              setTimeout(() => searchRef.current?.focus(), 50)
            }}
          >
            <span
              className="w-[10px] h-[10px] rounded-full flex-shrink-0"
              style={{
                background: '#1f9f93',
                boxShadow: '0 10px 18px rgba(31,159,147,.26)',
              }}
            />
            <span className="hidden sm:block text-[14px] font-semibold" style={{ color: 'var(--color-text-faint)' }}>
              {t('search')}
            </span>
          </div>

          {/* User avatar + dropdown */}
          <div className="relative" ref={userMenuRef}>
            <button
              className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full cursor-pointer transition-colors"
              style={{
                background: showUserMenu ? 'var(--color-glass-hover)' : 'var(--color-glass)',
                border: '1px solid var(--color-border)',
                boxShadow: 'var(--shadow-btn)',
              }}
              onClick={() => setShowUserMenu((v) => !v)}
            >
              <div
                className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #1f9f93, #2fb6aa)',
                }}
              >
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-[34px] h-[34px] rounded-full object-cover" />
                ) : (
                  <span className="text-[13px] font-bold text-white">
                    {(user?.first_name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                  </span>
                )}
              </div>
              <span className="hidden sm:block text-[13px] font-bold" style={{ color: 'var(--color-text)' }}>
                {user?.first_name || user?.email?.split('@')[0] || ''}
              </span>
            </button>

            {showUserMenu && (
              <div
                className="absolute right-0 top-full mt-2 w-48 rounded-xl overflow-hidden z-50"
                style={{
                  background: 'var(--color-overlay)',
                  border: '1px solid var(--color-border)',
                  boxShadow: '0 12px 32px rgba(0,0,0,.18)',
                }}
              >
                <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <p className="text-[13px] font-bold truncate" style={{ color: 'var(--color-text)' }}>
                    {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username}
                  </p>
                  <p className="text-[11px] truncate" style={{ color: 'var(--color-text-faint)' }}>
                    {user?.email}
                  </p>
                </div>
                <div className="py-1">
                  <button
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] font-semibold transition-colors hover:bg-[var(--color-hover)]"
                    style={{ color: 'var(--color-text)' }}
                    onClick={() => { navigate('/profile'); setShowUserMenu(false) }}
                  >
                    <UserIcon className="h-4 w-4" style={{ color: 'var(--color-text-faint)' }} />
                    {t('nav.profile')}
                  </button>
                  <button
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] font-semibold transition-colors hover:bg-[var(--color-hover)]"
                    style={{ color: '#e55353' }}
                    onClick={() => { logout(); setShowUserMenu(false) }}
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    {t('logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search Overlay */}
      {showSearch && (
        <>
          <div
            className="fixed inset-0 z-50"
            style={{ background: 'rgba(0,0,0,.20)' }}
            onClick={closeSearch}
          />
          <div className="fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-lg z-50 px-4">
            <div
              className="overflow-hidden"
              style={{
                background: 'var(--color-surface)',
                borderRadius: '16px',
                boxShadow: '0 25px 50px rgba(20,30,40,.25)',
                border: '1px solid var(--color-border-strong)',
              }}
            >
              {/* Search input */}
              <div
                className="flex items-center gap-3 px-4 py-3"
                style={{ borderBottom: '1px solid var(--color-border)' }}
              >
                <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--color-text-faint)' }} />
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  className="flex-1 bg-transparent text-sm font-semibold outline-none"
                  style={{ color: 'var(--color-text)' }}
                />
                {searchLoading && debouncedQuery.length >= 2 && (
                  <div
                    className="w-4 h-4 border-2 rounded-full animate-spin flex-shrink-0"
                    style={{ borderColor: 'var(--color-border)', borderTopColor: '#1f9f93' }}
                  />
                )}
                <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded flex-shrink-0" style={{
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-faint)',
                }}>
                  ESC
                </kbd>
              </div>

              {/* Results area */}
              <div className="max-h-[60vh] overflow-y-auto">
                {!searchQuery && (
                  <div className="px-4 py-6 text-center text-[13px] font-semibold" style={{ color: 'var(--color-text-faint)' }}>
                    {t('searchHint')}
                  </div>
                )}

                {searchQuery && debouncedQuery.length < 2 && (
                  <div className="px-4 py-6 text-center text-[13px] font-semibold" style={{ color: 'var(--color-text-faint)' }}>
                    {t('searchMinChars')}
                  </div>
                )}

                {searchError && (
                  <div className="px-4 py-6 text-center text-[13px] font-semibold" style={{ color: '#e55353' }}>
                    {t('searchError')}
                  </div>
                )}

                {debouncedQuery.length >= 2 && !searchLoading && results && !hasResults && (
                  <div className="px-4 py-6 text-center text-[13px] font-semibold" style={{ color: 'var(--color-text-faint)' }}>
                    {t('searchNoResults', { query: debouncedQuery })}
                  </div>
                )}

                {hasResults && SECTION_CONFIG.map(({ key, label, Icon, color }) => {
                  const items = results[key]
                  if (!items || items.length === 0) return null

                  return (
                    <div key={key} className="px-3 py-2">
                      <div className="flex items-center gap-2 px-1 py-1.5 mb-1">
                        <Icon className="h-4 w-4" style={{ color }} />
                        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-faint)' }}>
                          {label}
                        </span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{
                          background: 'var(--color-hover)',
                          color: 'var(--color-text-faint)',
                        }}>
                          {items.length}
                        </span>
                      </div>
                      {items.map((item) => (
                        <SearchResultItem
                          key={item.id}
                          item={item}
                          section={key}
                          onClick={() => handleResultClick(key, item)}
                        />
                      ))}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default Header
